import { useMemo } from 'react'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import type {
  AnalyticsItem,
  ProductItem,
} from 'src/components/restructure/product/types/productItem'

import type { ProductQueryObject } from './types/amplitudeObjects'

const AMPLITUDE_DENY_KEYS = ['carc quantity', 'cac quantity']

const normalizeAmplitudeKeys = (
  data?: Record<string, unknown>
): ProductQueryObject => {
  if (!data) return {}

  return Object.fromEntries(
    Object.entries(data)
      .map(([key, value]) => [key.replace(/_/g, ' '), value])
      .filter(([key]) => !AMPLITUDE_DENY_KEYS.includes(key as string))
  )
}

export function useProductObject() {
  const { orderForm } = useCheckout()

  return useMemo(() => {
    return orderForm?.items?.map((e) => {
      return {
        ...normalizeAmplitudeKeys(
          e?.additionalInformation?.analytics?.amplitude
        ),
      }
    })
  }, [orderForm?.items])
}

export function getProductQueryObject(
  currentSku: ProductItem | AnalyticsItem['amplitude'] | undefined
) {
  if (!currentSku) {
    return null
  }

  try {
    let amplitude = null

    if ('analytics' in currentSku && currentSku.analytics?.amplitude) {
      amplitude = currentSku.analytics.amplitude
    } else {
      amplitude = currentSku as AnalyticsItem['amplitude']
    }

    return {
      'product id': amplitude?.product_id,
      name: amplitude?.name,
      'product type': amplitude?.product_type,
      'seller name': amplitude?.seller_name,
      'seller id': amplitude?.seller_id,
      brand: amplitude?.brand,
      sport: amplitude?.sport,
      universe: amplitude?.universe,
      department: amplitude?.department,
      'sub department': amplitude?.sub_department,
      family: amplitude?.family,
      'list price': amplitude?.list_price,
      currency: amplitude?.currency,
      'display price': amplitude?.display_price,
      gender: amplitude?.gender,
      color: amplitude?.color,
      availability: amplitude?.availability,
      'sku code': amplitude?.sku_code,
      size: amplitude?.size,
      quantity: amplitude?.quantity,
      'purchase price': amplitude?.purchase_price,
      'purchase value': amplitude?.purchase_value,
      'sport group': amplitude?.sport_group,
      arborescence: amplitude?.arborescence,
      variant: amplitude?.variant,
      kitlook: amplitude?.kitlook,
      'sku sap': amplitude?.sku_sap,
      'modelo sap': amplitude?.modelo_sap,
    }
  } catch (error) {
    console.error('Error normalizing product object for amplitude:', error)

    return {}
  }
}
